import { evaluate } from "@kyan/ui";

import { type AccountStateByPortfolioSymbol } from "#/types/accounts";
import { PortfolioSymbol, type UnderlyingSymbol } from "#/types/markets";

import {
  type AccountRiskStatus,
  type AccountRiskStatusValues,
  type LiquidationWarningDetails,
  type LiquidationWarnings,
  LiquidationWarningStatus,
  type LiquidationWarningThresholds,
} from "./liquidationWarnings.types";

const LIQUIDATION_THRESHOLD = 1;

export const getAccountRiskStatus = (
  accountState: AccountStateByPortfolioSymbol,
): AccountRiskStatus => {
  return Object.fromEntries(
    Object.entries(accountState).map(([symbol, { mmr, isLiquidated }]) => [
      symbol,
      { mmr, isLiquidated },
    ]),
  );
};

const getDefaultUnderlyingLiquidationWarnings = (
  liquidationWarningThresholds: number[],
) => {
  return [
    ...liquidationWarningThresholds,
    LIQUIDATION_THRESHOLD.toString(),
  ].reduce<LiquidationWarningThresholds>((acc, threshold) => {
    acc[threshold] = LiquidationWarningStatus.Inactive;
    return acc;
  }, {});
};

export const getDefaultLiquidationWarnings = (
  liquidationWarningThresholds: number[],
): LiquidationWarnings => {
  const underlyingSymbols = Object.values(PortfolioSymbol).filter(
    (symbol) => symbol !== PortfolioSymbol.USDC,
  );

  return Object.fromEntries(
    underlyingSymbols.map((symbol) => [
      symbol,
      getDefaultUnderlyingLiquidationWarnings(liquidationWarningThresholds),
    ]),
  );
};

/**
 * Handles threshold recalculation when isLiquidated flag is true.
 * This is the ONLY way the liquidation threshold (1.0) becomes Active.
 * All warning thresholds below 1.0 are dismissed, and the 1.0 threshold is activated.
 */
const recalculateThresholdsWithLiquidation = (
  thresholds: LiquidationWarningThresholds,
): LiquidationWarningThresholds => {
  return Object.fromEntries(
    Object.entries(thresholds).map(([threshold]) => {
      const isAlreadyDismissed =
        thresholds[threshold] === LiquidationWarningStatus.Dismissed;
      const isBelowLiquidationThreshold =
        parseFloat(threshold) < LIQUIDATION_THRESHOLD;

      return [
        threshold,
        isAlreadyDismissed || isBelowLiquidationThreshold
          ? LiquidationWarningStatus.Dismissed
          : LiquidationWarningStatus.Active,
      ];
    }),
  );
};

const recalculateThresholds = (
  thresholds: LiquidationWarningThresholds,
  isLiquidated: boolean,
): LiquidationWarningThresholds => {
  if (isLiquidated) return recalculateThresholdsWithLiquidation(thresholds);

  const highestActiveOrDismissed = Math.max(
    ...Object.entries(thresholds)
      .filter(([, status]) =>
        [
          LiquidationWarningStatus.Active,
          LiquidationWarningStatus.Dismissed,
        ].includes(status),
      )
      .map(([threshold]) => parseFloat(threshold)),
  );

  return Object.fromEntries(
    Object.entries(thresholds).map(([threshold, status]) => [
      threshold,
      parseFloat(threshold) < highestActiveOrDismissed
        ? LiquidationWarningStatus.Dismissed
        : status,
    ]),
  );
};

const recalculateThresholdsWithAccountRiskStatus = (
  thresholds: LiquidationWarningThresholds,
  accountRiskStatus: AccountRiskStatusValues,
): LiquidationWarningThresholds => {
  if (accountRiskStatus.isLiquidated)
    return recalculateThresholdsWithLiquidation(thresholds);

  const newThresholds = Object.fromEntries(
    Object.entries(thresholds).map(([threshold, status]) => {
      if (status === LiquidationWarningStatus.Dismissed)
        return [threshold, status];

      const thresholdValue = parseFloat(threshold);

      // The liquidation threshold (1.0) is excluded from MMR-based activation.
      // Only the isLiquidated flag can trigger the liquidation threshold to become Active.
      // Even if MMR >= 1.0, only warning thresholds (e.g., 0.9) become Active, never the 1.0 threshold.
      return [
        threshold,
        thresholdValue <= accountRiskStatus.mmr &&
        thresholdValue !== LIQUIDATION_THRESHOLD
          ? LiquidationWarningStatus.Active
          : status,
      ];
    }),
  );

  return recalculateThresholds(newThresholds, false);
};

export const updateLiquidationWarningsWithDefault = (
  defaultWarnings: LiquidationWarnings,
  currentWarnings: LiquidationWarnings,
  accountRiskStatus: AccountRiskStatus,
): LiquidationWarnings => {
  if (!Object.keys(currentWarnings).length) return defaultWarnings;

  return Object.fromEntries(
    Object.entries(currentWarnings).map(([symbol, currentThresholds]) => {
      const underlyingSymbol = symbol as UnderlyingSymbol;
      const baseThresholds = defaultWarnings[underlyingSymbol] ?? {};

      const filteredCurrentThresholds = Object.fromEntries(
        Object.entries(currentThresholds).filter(
          ([threshold]) => threshold in baseThresholds,
        ),
      );

      const mergedThresholds = {
        ...baseThresholds,
        ...filteredCurrentThresholds,
      };
      const recalculatedThresholds = recalculateThresholds(
        mergedThresholds,
        accountRiskStatus[underlyingSymbol]!.isLiquidated,
      );

      return [symbol, recalculatedThresholds];
    }),
  );
};

export const updateLiquidationWarningsWithAccountRiskStatus = (
  liquidationWarnings: LiquidationWarnings,
  accountRiskStatus: AccountRiskStatus,
): LiquidationWarnings => {
  return Object.fromEntries(
    Object.entries(liquidationWarnings).map(([symbol, thresholds]) => [
      symbol,
      recalculateThresholdsWithAccountRiskStatus(
        thresholds,
        accountRiskStatus[symbol as UnderlyingSymbol]!,
      ),
    ]),
  );
};

export const flipActiveToDismissed = (
  currentWarnings: LiquidationWarnings,
  symbol: UnderlyingSymbol,
): LiquidationWarnings => {
  const symbolThresholds = currentWarnings[symbol];

  if (!symbolThresholds) return currentWarnings;

  const updatedThresholds = Object.fromEntries(
    Object.entries(symbolThresholds).map(([threshold, status]) => [
      threshold,
      status === LiquidationWarningStatus.Active
        ? LiquidationWarningStatus.Dismissed
        : status,
    ]),
  );

  return {
    ...currentWarnings,
    [symbol]: updatedThresholds,
  };
};

export const getLiquidationWarningDetails = (
  liquidationWarnings: LiquidationWarnings,
  accountRiskStatus: AccountRiskStatus,
): LiquidationWarningDetails[] => {
  return Object.entries(liquidationWarnings).map(([symbol, thresholds]) => {
    const isWarning = Object.entries(thresholds)
      .filter(([threshold]) => threshold !== LIQUIDATION_THRESHOLD.toString())
      .some(([_, status]) => {
        return status === LiquidationWarningStatus.Active;
      });
    const isLiquidation =
      thresholds[LIQUIDATION_THRESHOLD] === LiquidationWarningStatus.Active;
    const variant =
      evaluate({
        warning: isWarning,
        liquidation: isLiquidation,
      }) ?? "none";

    return {
      symbol: symbol as UnderlyingSymbol,
      mmr: accountRiskStatus[symbol as UnderlyingSymbol]!.mmr,
      variant,
    };
  });
};
